import { useEditor, EditorContent } from '@tiptap/react';
import { toast } from './Toast';
import { askPrompt } from './ui';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import { Table } from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import CharacterCount from '@tiptap/extension-character-count';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Typography from '@tiptap/extension-typography';
import FontFamily from '@tiptap/extension-font-family';
import { useEffect, useCallback, useState, useRef, lazy, Suspense } from 'react';
import { Sparkles, Loader2, ImagePlus, TableIcon, Smile, Undo2, Redo2 } from 'lucide-react';
import { authAxios } from '../services/apiClient';

const EmojiPicker = lazy(() => import('@emoji-mart/react').then(m => ({ default: m.default })));

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  maxLength?: number;
}

function ToolbarButton({ active, onClick, title, children, disabled }: {
  active?: boolean;
  onClick: () => void;
  title: string;
  children: React.ReactNode;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      disabled={disabled}
      className={`p-1.5 rounded text-sm transition-colors ${
        disabled ? 'text-gray-300 cursor-not-allowed'
        : active ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'
      }`}
    >
      {children}
    </button>
  );
}

function Divider() {
  return <div className="w-px h-5 bg-gray-300 mx-1" />;
}

const AI_ACTIONS = [
  { key: 'improve', label: 'Popraw styl' },
  { key: 'proofread', label: 'Korekta' },
  { key: 'shorten', label: 'Skroc' },
  { key: 'expand', label: 'Rozwiń' },
  { key: 'translate_en', label: 'EN' },
  { key: 'translate_pl', label: 'PL' },
];

const FONT_FAMILIES = [
  { value: '', label: 'Domyslna' },
  { value: 'Inter', label: 'Inter' },
  { value: 'Arial', label: 'Arial' },
  { value: 'Georgia', label: 'Georgia' },
  { value: 'Courier New', label: 'Courier' },
  { value: 'Times New Roman', label: 'Times' },
];

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Wprowadź tekst...',
  maxLength,
}) => {
  const [aiLoading, setAiLoading] = useState(false);
  const [aiMenuOpen, setAiMenuOpen] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const aiRef = useRef<HTMLDivElement>(null);
  const emojiRef = useRef<HTMLDivElement>(null);
  const [emojiData, setEmojiData] = useState<any>(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Underline,
      Subscript,
      Superscript,
      Link.configure({ openOnClick: false }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      TextStyle,
      Color,
      FontFamily,
      Highlight.configure({ multicolor: true }),
      Table.configure({ resizable: true }),
      TableRow,
      TableCell,
      TableHeader,
      Image.configure({ inline: true, allowBase64: true }),
      Placeholder.configure({ placeholder }),
      ...(maxLength ? [CharacterCount.configure({ limit: maxLength })] : [CharacterCount]),
      TaskList,
      TaskItem.configure({ nested: true }),
      Typography,
    ],
    content: value || '',
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none min-h-[200px] px-3 py-2',
      },
    },
    onUpdate: ({ editor: ed }) => {
      onChange(ed.getHTML());
    },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '');
    }
  }, [value, editor]);

  // Close dropdowns on outside click
  useEffect(() => {
    const close = (e: MouseEvent) => {
      if (aiRef.current && !aiRef.current.contains(e.target as Node)) setAiMenuOpen(false);
      if (emojiRef.current && !emojiRef.current.contains(e.target as Node)) setEmojiOpen(false);
    };
    if (aiMenuOpen || emojiOpen) document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [aiMenuOpen, emojiOpen]);

  // Lazy load emoji data
  useEffect(() => {
    if (emojiOpen && !emojiData) {
      import('@emoji-mart/data').then(m => setEmojiData(m.default));
    }
  }, [emojiOpen, emojiData]);

  const setLink = useCallback(async () => {
    if (!editor) return;
    const prev = editor.getAttributes('link').href;
    const url = await askPrompt({ title: 'Link', message: 'Podaj adres URL (puste = usuń link):', defaultValue: prev || 'https://' });
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
  }, [editor]);

  const insertImage = useCallback(async () => {
    if (!editor) return;
    const url = await askPrompt({ title: 'Obrazek', message: 'Podaj adres URL obrazka:', defaultValue: 'https://' });
    if (url) editor.chain().focus().setImage({ src: url }).run();
  }, [editor]);

  const insertTable = useCallback(() => {
    if (!editor) return;
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  }, [editor]);

  const handleEmojiSelect = useCallback((emoji: any) => {
    if (!editor) return;
    editor.chain().focus().insertContent(emoji.native).run();
    setEmojiOpen(false);
  }, [editor]);

  const handleAi = useCallback(async (action: string, customPrompt?: string) => {
    if (!editor || aiLoading) return;
    setAiMenuOpen(false);
    setAiLoading(true);
    try {
      const { from, to } = editor.state.selection;
      const hasSelection = from !== to;
      const text = hasSelection
        ? editor.state.doc.textBetween(from, to, '\n')
        : editor.getText();
      if (!text.trim()) { setAiLoading(false); return; }

      const { data } = await authAxios.post('/api/ai/text-edit', { text, action, customPrompt });
      if (data.result) {
        if (hasSelection) {
          editor.chain().focus().deleteSelection().insertContent(data.result).run();
        } else {
          editor.commands.setContent(data.result);
        }
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Błąd AI');
    } finally {
      setAiLoading(false);
    }
  }, [editor, aiLoading]);

  if (!editor) return null;

  const charCount = editor.storage.characterCount?.characters() ?? 0;
  const wordCount = editor.storage.characterCount?.words() ?? 0;

  return (
    <div className="border rounded-lg overflow-hidden bg-white">
      {/* Toolbar row 1 — formatting */}
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b bg-gray-50">
        {/* Undo/Redo */}
        <ToolbarButton onClick={() => editor.chain().focus().undo().run()} title="Cofnij" disabled={!editor.can().undo()}>
          <Undo2 size={14} />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().redo().run()} title="Ponow" disabled={!editor.can().redo()}>
          <Redo2 size={14} />
        </ToolbarButton>

        <Divider />

        {/* Heading select */}
        <select
          value={
            editor.isActive('heading', { level: 1 }) ? '1' :
            editor.isActive('heading', { level: 2 }) ? '2' :
            editor.isActive('heading', { level: 3 }) ? '3' : '0'
          }
          onChange={e => {
            const level = parseInt(e.target.value);
            if (level === 0) editor.chain().focus().setParagraph().run();
            else editor.chain().focus().toggleHeading({ level: level as 1 | 2 | 3 }).run();
          }}
          className="text-xs border rounded px-1.5 py-1 bg-white"
        >
          <option value="0">Paragraf</option>
          <option value="1">H1</option>
          <option value="2">H2</option>
          <option value="3">H3</option>
        </select>

        {/* Font family */}
        <select
          value={editor.getAttributes('textStyle').fontFamily || ''}
          onChange={e => {
            if (e.target.value) editor.chain().focus().setFontFamily(e.target.value).run();
            else editor.chain().focus().unsetFontFamily().run();
          }}
          className="text-xs border rounded px-1.5 py-1 bg-white"
        >
          {FONT_FAMILIES.map(f => (
            <option key={f.value} value={f.value}>{f.label}</option>
          ))}
        </select>

        <Divider />

        {/* Text formatting */}
        <ToolbarButton active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} title="Pogrubienie (Ctrl+B)">
          <strong>B</strong>
        </ToolbarButton>
        <ToolbarButton active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} title="Kursywa (Ctrl+I)">
          <em>I</em>
        </ToolbarButton>
        <ToolbarButton active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()} title="Podkreslenie (Ctrl+U)">
          <span className="underline">U</span>
        </ToolbarButton>
        <ToolbarButton active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()} title="Przekreslenie">
          <span className="line-through">S</span>
        </ToolbarButton>
        <ToolbarButton active={editor.isActive('subscript')} onClick={() => editor.chain().focus().toggleSubscript().run()} title="Indeks dolny">
          <span className="text-[10px]">X<sub>2</sub></span>
        </ToolbarButton>
        <ToolbarButton active={editor.isActive('superscript')} onClick={() => editor.chain().focus().toggleSuperscript().run()} title="Indeks gorny">
          <span className="text-[10px]">X<sup>2</sup></span>
        </ToolbarButton>

        <Divider />

        {/* Highlight */}
        <label className="relative cursor-pointer p-1.5" title="Podswietlenie">
          <span className="text-sm px-0.5 bg-yellow-200 rounded">H</span>
          <input type="color" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            value="#fef08a"
            onChange={e => editor.chain().focus().toggleHighlight({ color: e.target.value }).run()} />
        </label>

        {/* Text color */}
        <label className="relative cursor-pointer p-1.5" title="Kolor tekstu">
          <span className="text-sm" style={{ color: editor.getAttributes('textStyle').color || '#000' }}>A</span>
          <input type="color" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            value={editor.getAttributes('textStyle').color || '#000000'}
            onChange={e => editor.chain().focus().setColor(e.target.value).run()} />
        </label>

        <Divider />

        {/* Lists */}
        <ToolbarButton active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Lista punktowana">
          &#8226;
        </ToolbarButton>
        <ToolbarButton active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Lista numerowana">
          1.
        </ToolbarButton>
        <ToolbarButton active={editor.isActive('taskList')} onClick={() => editor.chain().focus().toggleTaskList().run()} title="Lista zadan">
          &#9745;
        </ToolbarButton>

        <Divider />

        {/* Alignment */}
        <ToolbarButton active={editor.isActive({ textAlign: 'left' })} onClick={() => editor.chain().focus().setTextAlign('left').run()} title="Lewo">
          &#9776;
        </ToolbarButton>
        <ToolbarButton active={editor.isActive({ textAlign: 'center' })} onClick={() => editor.chain().focus().setTextAlign('center').run()} title="Srodek">
          &#9783;
        </ToolbarButton>
        <ToolbarButton active={editor.isActive({ textAlign: 'right' })} onClick={() => editor.chain().focus().setTextAlign('right').run()} title="Prawo">
          &#9782;
        </ToolbarButton>

        <Divider />

        {/* Link */}
        <ToolbarButton active={editor.isActive('link')} onClick={setLink} title="Wstaw link">
          &#128279;
        </ToolbarButton>

        {/* Image */}
        <ToolbarButton onClick={insertImage} title="Wstaw obrazek">
          <ImagePlus size={14} />
        </ToolbarButton>

        {/* Table */}
        <ToolbarButton active={editor.isActive('table')} onClick={insertTable} title="Wstaw tabele 3x3">
          <TableIcon size={14} />
        </ToolbarButton>

        {/* Blockquote */}
        <ToolbarButton active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()} title="Cytat">
          &#10077;
        </ToolbarButton>

        {/* Code block */}
        <ToolbarButton active={editor.isActive('codeBlock')} onClick={() => editor.chain().focus().toggleCodeBlock().run()} title="Blok kodu">
          {'</>'}
        </ToolbarButton>

        {/* Horizontal rule */}
        <ToolbarButton onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Linia pozioma">
          &#8213;
        </ToolbarButton>

        <Divider />

        {/* Emoji picker */}
        <div className="relative" ref={emojiRef}>
          <ToolbarButton onClick={() => setEmojiOpen(!emojiOpen)} title="Emotikony">
            <Smile size={14} />
          </ToolbarButton>
          {emojiOpen && emojiData && (
            <div className="absolute top-full left-0 mt-1 z-50">
              <Suspense fallback={<div className="bg-white border rounded-lg p-4 text-xs text-gray-400">Ładowanie...</div>}>
                <EmojiPicker data={emojiData} onEmojiSelect={handleEmojiSelect} locale="pl" theme="light" previewPosition="none" skinTonePosition="none" />
              </Suspense>
            </div>
          )}
        </div>

        {/* Clear formatting */}
        <ToolbarButton onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()} title="Wyczysc formatowanie">
          &#10005;
        </ToolbarButton>

        <Divider />

        {/* AI dropdown */}
        <div className="relative" ref={aiRef}>
          <button type="button" onClick={() => setAiMenuOpen(!aiMenuOpen)} disabled={aiLoading}
            title="Edycja AI"
            className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${
              aiLoading ? 'bg-purple-100 text-purple-400 cursor-wait'
                : aiMenuOpen ? 'bg-purple-100 text-purple-700'
                : 'bg-purple-50 text-purple-600 hover:bg-purple-100'
            }`}>
            {aiLoading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            AI
          </button>
          {aiMenuOpen && (
            <div className="absolute top-full right-0 mt-1 bg-white border rounded-lg shadow-lg z-50 py-1 min-w-[150px]">
              {AI_ACTIONS.map(a => (
                <button key={a.key} type="button" onClick={() => handleAi(a.key)}
                  className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50">
                  {a.label}
                </button>
              ))}
              <div className="border-t my-1" />
              <button type="button"
                onClick={async () => {
                  setAiMenuOpen(false);
                  const p = await askPrompt({ title: 'Instrukcja AI', placeholder: 'np. skróć i uprość ten tekst', multiline: true });
                  if (p) handleAi('custom', p);
                }}
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 text-purple-600">
                Własna instrukcja...
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Table controls (shown when inside a table) */}
      {editor.isActive('table') && (
        <div className="flex items-center gap-1 px-2 py-1 border-b bg-blue-50 text-xs">
          <span className="text-blue-600 font-medium mr-1">Tabela:</span>
          <button type="button" onClick={() => editor.chain().focus().addColumnBefore().run()} className="px-1.5 py-0.5 bg-white border rounded hover:bg-blue-100 text-blue-700">+Kolumna przed</button>
          <button type="button" onClick={() => editor.chain().focus().addColumnAfter().run()} className="px-1.5 py-0.5 bg-white border rounded hover:bg-blue-100 text-blue-700">+Kolumna po</button>
          <button type="button" onClick={() => editor.chain().focus().addRowBefore().run()} className="px-1.5 py-0.5 bg-white border rounded hover:bg-blue-100 text-blue-700">+Wiersz przed</button>
          <button type="button" onClick={() => editor.chain().focus().addRowAfter().run()} className="px-1.5 py-0.5 bg-white border rounded hover:bg-blue-100 text-blue-700">+Wiersz po</button>
          <button type="button" onClick={() => editor.chain().focus().deleteColumn().run()} className="px-1.5 py-0.5 bg-white border rounded hover:bg-red-100 text-red-600">-Kolumna</button>
          <button type="button" onClick={() => editor.chain().focus().deleteRow().run()} className="px-1.5 py-0.5 bg-white border rounded hover:bg-red-100 text-red-600">-Wiersz</button>
          <button type="button" onClick={() => editor.chain().focus().deleteTable().run()} className="px-1.5 py-0.5 bg-white border rounded hover:bg-red-100 text-red-600">Usuń tabele</button>
          <button type="button" onClick={() => editor.chain().focus().mergeCells().run()} className="px-1.5 py-0.5 bg-white border rounded hover:bg-blue-100 text-blue-700">Połącz</button>
          <button type="button" onClick={() => editor.chain().focus().splitCell().run()} className="px-1.5 py-0.5 bg-white border rounded hover:bg-blue-100 text-blue-700">Rozdziel</button>
        </div>
      )}

      {/* Editor content */}
      <EditorContent editor={editor} />

      {/* Status bar */}
      <div className="flex items-center justify-between px-3 py-1 border-t bg-gray-50 text-[10px] text-gray-400">
        <div className="flex items-center gap-3">
          <span>{charCount} znakow</span>
          <span>{wordCount} slow</span>
          {maxLength && (
            <span className={charCount > maxLength * 0.9 ? 'text-red-500 font-medium' : ''}>
              {maxLength - charCount} pozostalo
            </span>
          )}
        </div>
        {aiLoading && (
          <div className="flex items-center gap-1 text-purple-500">
            <Loader2 size={10} className="animate-spin" />
            AI przetwarza...
          </div>
        )}
      </div>
    </div>
  );
};
